// Redux slice caching the JVVNL search index (static + dynamic entities).
// Persisted to localStorage with a 1-hour TTL so repeat opens are instant.

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  getJvvnlFeeders,
  getJvvnlPlants,
  getJvvnlSubstations,
  getJvvnlDiscomSubstations,
  getJvvnlCircles,
} from "../../../Rest_api/RestApi";
import { SearchItem } from "../../../components/Search/Jvvnl/types";
import { STATIC_INDEX } from "../../../components/Search/Jvvnl/staticIndex";

const CACHE_KEY = "jvvnl_search_index_v14";
const TTL_MS = 60 * 60 * 1000; // 1 hour

interface CachePayload {
  items: SearchItem[];
  ts: number;
}

function readCache(): CachePayload | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachePayload;
    if (!parsed || !Array.isArray(parsed.items)) return null;
    if (typeof parsed.ts !== "number" || Date.now() - parsed.ts > TTL_MS) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

// Cap on how many items we persist. A very large feeder set can blow the ~5MB
// localStorage quota; beyond this we keep the in-memory index but skip the
// localStorage cache (re-fetch on next session) rather than failing every write.
const MAX_CACHED_ITEMS = 6000;

function writeCache(items: SearchItem[]): void {
  if (items.length > MAX_CACHED_ITEMS) return;
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ items, ts: Date.now() } as CachePayload),
    );
  } catch {
    // localStorage full / unavailable — degrade silently, slice still works.
  }
}

const OVERVIEW_PAGE = "jvvnl/jvvnl-generation-load-balance";
const OVERVIEW_LABEL = "Net Load & DRE Flow";
const METRIC_PAGE = "jvvnl/jvvnl-metric";
const METRIC_LABEL = "Metric — Net Load & DRE Flow";

/**
 * Build the Metric page's own URL params for a granularity + entity, including
 * any parent selections so the page shows the full hierarchy (circle → transco
 * → discom → feeder), matching what the manual dropdown cascade would set.
 */
function metricQuery(
  granularity: "Circle" | "Transco" | "Discom Substation" | "Feeder",
  idKey: "circle" | "transco" | "discom" | "feeder",
  id: number | string,
  label: string,
  parents?: {
    transco?: { id: number; label: string };
    discom?: { id: number; label: string };
  },
): string {
  const p = new URLSearchParams();
  p.set("granularity", granularity);
  p.set(idKey, String(id));
  p.set(`${idKey}_label`, label);
  if (parents?.transco) {
    p.set("transco", String(parents.transco.id));
    p.set("transco_label", parents.transco.label);
  }
  if (parents?.discom) {
    p.set("discom", String(parents.discom.id));
    p.set("discom_label", parents.discom.label);
  }
  return p.toString();
}

function buildDynamicIndex(
  feeders: Awaited<ReturnType<typeof getJvvnlFeeders>>,
  plants: Awaited<ReturnType<typeof getJvvnlPlants>>,
  substations: Awaited<ReturnType<typeof getJvvnlSubstations>>,
  discomSubs: Awaited<ReturnType<typeof getJvvnlDiscomSubstations>>,
  circles: Awaited<ReturnType<typeof getJvvnlCircles>>,
): SearchItem[] {
  const items: SearchItem[] = [];

  // Lookup: discom_substation_id → { discom info + its parent transco }.
  // Used to backfill the parent chain into feeder/discom Metric deep-links.
  const discomById = new Map(
    discomSubs.map((d) => [d.discom_substation_id, d]),
  );

  // Emit a Chart variant (Net Load page) and a Metric variant (Metric page) for
  // the same entity, unified into a single search item with hasDualViews if both exist.
  const pushBoth = (opts: {
    idBase: string;
    label: string;
    searchText: string;
    category: string;
    priority: number;
    hint: string;
    mapping?: string;
    chartDeepLink: SearchItem["deepLink"];
    metric: { query: string } | null; // null = no Metric equivalent
  }) => {
    if (opts.metric && opts.chartDeepLink) {
      items.push({
        id: opts.idBase,
        type: "entity",
        label: opts.label,
        searchText: `${opts.searchText} chart metric`,
        page: OVERVIEW_PAGE,
        pageLabel: OVERVIEW_LABEL,
        category: opts.category,
        priority: opts.priority,
        hint: opts.hint,
        mapping: opts.mapping,
        hasDualViews: true,
        chartTarget: {
          page: OVERVIEW_PAGE,
          pageLabel: OVERVIEW_LABEL,
          deepLink: opts.chartDeepLink,
        },
        metricTarget: {
          page: METRIC_PAGE,
          pageLabel: METRIC_LABEL,
          directQuery: opts.metric.query,
        },
      });
    } else {
      if (opts.chartDeepLink) {
        items.push({
          id: `${opts.idBase}-chart`,
          type: "entity",
          label: opts.label,
          searchText: `${opts.searchText} chart`,
          page: OVERVIEW_PAGE,
          pageLabel: OVERVIEW_LABEL,
          category: opts.category,
          priority: opts.priority,
          hint: opts.hint,
          mapping: opts.mapping,
          viewKind: "Chart",
          deepLink: opts.chartDeepLink,
        });
      } else if (opts.metric) {
        items.push({
          id: `${opts.idBase}-metric`,
          type: "entity",
          label: opts.label,
          searchText: `${opts.searchText} metric`,
          page: METRIC_PAGE,
          pageLabel: METRIC_LABEL,
          category: opts.category,
          priority: opts.priority,
          hint: opts.hint,
          mapping: opts.mapping,
          viewKind: "Metric",
          directQuery: opts.metric.query,
        });
      }
    }
  };

  for (const f of feeders) {
    if (!f.feeder_name) continue;
    // Resolve parent discom + transco for the Metric deep-link hierarchy and breadcrumbs.
    const discom =
      f.discom_substation_id != null
        ? discomById.get(f.discom_substation_id)
        : undefined;
    
    const transcoName = discom?.transco_substation_name || "";
    const discomName = discom?.discom_substation_name || f.discom_substation_name || "";
    const parentChain = [transcoName, discomName, f.location_name]
      .filter(Boolean)
      .join(" > ");

    const parents =
      discom != null
        ? {
            discom: {
              id: discom.discom_substation_id,
              label:
                discom.discom_substation_name ||
                f.discom_substation_name ||
                "",
            },
            ...(discom.transco_substation_id != null
              ? {
                  transco: {
                    id: discom.transco_substation_id,
                    label: discom.transco_substation_name ?? "",
                  },
                }
              : {}),
          }
        : undefined;
    pushBoth({
      idBase: `feeder-${f.feeder_id}`,
      label: f.feeder_name,
      searchText:
        `${f.feeder_name} ${f.discom_substation_name ?? ""} ${f.location_name ?? ""} feeder`.toLowerCase(),
      category: "Feeder",
      priority: 4,
      hint: "Feeder",
      mapping: parentChain || undefined,
      chartDeepLink: {
        groupBy: "feeder",
        value: f.feeder_id,
        label: f.feeder_name,
      },
      metric: {
        query: metricQuery(
          "Feeder",
          "feeder",
          f.feeder_id,
          f.feeder_name,
          parents,
        ),
      },
    });
  }

  // Plants: Chart only — the Metric page has no plant granularity.
  for (const p of plants) {
    if (!p.plant_name) continue;
    pushBoth({
      idBase: `plant-${p.plant_id}`,
      label: p.plant_name,
      searchText:
        `${p.plant_name} ${p.plant_type ?? ""} ${p.location_name ?? ""} plant generation`.toLowerCase(),
      category: "Plant",
      priority: 4,
      hint: "Plant",
      mapping:
        [p.plant_type, p.location_name].filter(Boolean).join(" · ") ||
        undefined,
      chartDeepLink: {
        groupBy: "plant",
        value: p.plant_id,
        label: p.plant_name,
        plantType: p.plant_type,
      },
      metric: null,
    });
  }

  // Transco substations.
  for (const s of substations) {
    if (!s.transco_substation_name) continue;
    pushBoth({
      idBase: `substation-${s.transco_substation_id}`,
      label: s.transco_substation_name,
      searchText:
        `${s.transco_substation_name} ${s.district ?? ""} ${s.divisional_headquarter ?? ""} transco substation gss`.toLowerCase(),
      category: "Transco Substation",
      priority: 3,
      hint: "Transco Substation",
      mapping:
        [s.district, s.divisional_headquarter].filter(Boolean).join(" · ") ||
        undefined,
      chartDeepLink: {
        groupBy: "substation",
        value: s.transco_substation_id,
        label: s.transco_substation_name,
      },
      metric: {
        query: metricQuery(
          "Transco",
          "transco",
          s.transco_substation_id,
          s.transco_substation_name,
        ),
      },
    });
  }

  // Discom substations: Chart (group_discom_substation deep-link) + Metric.
  for (const d of discomSubs) {
    if (!d.discom_substation_name) continue;
    pushBoth({
      idBase: `discomsub-${d.discom_substation_id}`,
      label: d.discom_substation_name,
      searchText:
        `${d.discom_substation_name} ${d.transco_substation_name ?? ""} discom substation`.toLowerCase(),
      category: "Discom Substation",
      priority: 3,
      hint: "Discom Substation",
      mapping: d.transco_substation_name || undefined,
      chartDeepLink: {
        groupBy: "discom_substation",
        value: d.discom_substation_id,
        label: d.discom_substation_name,
      },
      metric: {
        query: metricQuery(
          "Discom Substation",
          "discom",
          d.discom_substation_id,
          d.discom_substation_name,
          d.transco_substation_id != null
            ? {
                transco: {
                  id: d.transco_substation_id,
                  label: d.transco_substation_name ?? "",
                },
              }
            : undefined,
        ),
      },
    });
  }

  // Circles: Chart (group_discom deep-link, circle pre-selected) + Metric.
  // Sourced from getJvvnlCircles (was previously a single hardcoded "KOTPUTLI").
  for (const c of circles) {
    if (!c.circle_name) continue;
    pushBoth({
      idBase: `circle-${c.circle_id}`,
      label: c.circle_name,
      searchText: `${c.circle_name} circle`.toLowerCase(),
      category: "Circle",
      priority: 2,
      hint: "Circle",
      chartDeepLink: {
        groupBy: "circle",
        value: c.circle_id,
        label: c.circle_name,
      },
      metric: {
        query: metricQuery("Circle", "circle", c.circle_id, c.circle_name),
      },
    });
  }

  return items;
}

export interface SearchIndexResult {
  items: SearchItem[];
  /** True when one or more dynamic sources failed → results are limited. */
  degraded: boolean;
}

// Run a fetch, logging + flagging failures so the UI can warn the user.
async function safeFetch<T>(
  source: string,
  fn: () => Promise<T[]>,
  failures: string[],
): Promise<T[]> {
  try {
    return await fn();
  } catch (e: any) {
    // eslint-disable-next-line no-console
    console.error(
      `[JvvnlSearchIndex] failed to load "${source}" @ ${new Date().toISOString()}:`,
      e?.message ?? e,
    );
    failures.push(source);
    return [];
  }
}

export const initializeJvvnlSearchIndex = createAsyncThunk<
  SearchIndexResult,
  { force?: boolean } | undefined
>("jvvnlSearchIndex/initialize", async (arg) => {
  if (!arg?.force) {
    const cached = readCache();
    if (cached) return { items: cached.items, degraded: false };
  }

  const failures: string[] = [];
  // These GETs are already memoized by Network_utility's in-memory cache.
  const [feeders, plants, substations, discomSubs, circles] = await Promise.all([
    safeFetch("feeders", getJvvnlFeeders, failures),
    safeFetch("plants", getJvvnlPlants, failures),
    safeFetch("substations", getJvvnlSubstations, failures),
    safeFetch("discom-substations", getJvvnlDiscomSubstations, failures),
    safeFetch("circles", getJvvnlCircles, failures),
  ]);

  const items = [
    ...STATIC_INDEX,
    ...buildDynamicIndex(feeders, plants, substations, discomSubs, circles),
  ];
  const degraded = failures.length > 0;
  // Only cache a complete index — don't persist a degraded one as if it were whole.
  if (!degraded) writeCache(items);
  return { items, degraded };
});

interface SearchIndexState {
  items: SearchItem[];
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;
  /** True when some dynamic data failed and results are limited/static. */
  degraded: boolean;
}

const initialState: SearchIndexState = {
  items: [],
  status: "idle",
  error: null,
  degraded: false,
};

const searchIndexSlice = createSlice({
  name: "jvvnlSearchIndex",
  initialState,
  reducers: {
    clearJvvnlSearchIndex(state) {
      state.items = [];
      state.status = "idle";
      try {
        localStorage.removeItem(CACHE_KEY);
      } catch {
        /* ignore */
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeJvvnlSearchIndex.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(
        initializeJvvnlSearchIndex.fulfilled,
        (state, action: PayloadAction<SearchIndexResult>) => {
          state.items = action.payload.items;
          state.degraded = action.payload.degraded;
          state.status = "ready";
        },
      )
      .addCase(initializeJvvnlSearchIndex.rejected, (state, action) => {
        state.status = "error";
        state.degraded = true;
        state.error = action.error.message ?? "Failed to load search index";
        // Fall back to static index so pages/metrics still searchable.
        if (state.items.length === 0) state.items = STATIC_INDEX;
      });
  },
});

export const { clearJvvnlSearchIndex } = searchIndexSlice.actions;
export default searchIndexSlice.reducer;
